import { FC } from 'react';
import { CircularProgress, Pagination, TableBody, TableCell, TableRow, Tooltip, styled } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';

import Text from '../Text';
import { BACKGROUND, PEARL, PRIMARY, STROKE, TEXT } from '../../Helpers/colors';
import { HeaderProperties } from './tableProperties';

export const HeaderTableCell = styled(TableCell)(({ id }) => ({
	height: '40px',
	padding: '8px 12px',
	backgroundColor: PEARL.QUATERNARY,
	borderBottom: `1px solid ${STROKE.PRIMARY}`,
	borderLeft: id === '0' ? `4px solid ${PEARL.QUATERNARY}` : 'none',
}));

export const CustomTableRow = styled(TableRow)(({ selected }) => ({
	backgroundColor: selected ? PRIMARY.QUATERNARY : BACKGROUND.PRIMARY[100],
	borderLeft: selected ? `4px solid ${PRIMARY.BASE}` : `none`,
	':hover': {
		backgroundColor: PEARL.QUATERNARY,
	},
}));

export const RowTableCell = styled(TableCell)(() => ({
	minHeight: '44px',
	height: 'max-content',
	padding: '12px',
	borderBottom: `1px solid ${STROKE.SECONDARY}`,
	color: TEXT.SECONDARY,
	fontFamily: 'Inter',
	fontSize: '14px',
	fontWeight: 500,
	lineHeight: '20px',
}));

export const HeaderContent: FC<{ item: HeaderProperties }> = ({ item }) => {
	const { id, name, orderBy, width, tooltip } = item;
	const [params, setParams] = useSearchParams();

	const isDescendingOrder = params.get('direction') === 'desc';
	const order = params.get('orderBy');

	const handleSort = (orderByParameter: string | undefined) => {
		if (typeof orderByParameter !== 'string') return;
		const direction = isDescendingOrder ? 'asc' : 'desc';
		params.set('direction', direction);
		params.set('orderBy', orderByParameter);
		setParams(params, { replace: true });
	};

	const content = (
		<div
			style={{
				display: 'flex',
				gap: 4,
				alignItems: 'center',
				width: 'max-content',
			}}
		>
			<Text title={name} fontWeight="medium" type="pxs" />
			{Boolean(orderBy) && (
				<FontAwesomeIcon
					icon={isDescendingOrder && order === orderBy ? faArrowUp : faArrowDown}
					style={{
						fontSize: 12,
						color: TEXT.SECONDARY,
					}}
				/>
			)}
		</div>
	);

	return (
		<HeaderTableCell id={id} key={id} onClick={() => handleSort(orderBy)} width={width}>
			{tooltip ? (
				<Tooltip title={tooltip} placement="top">
					<div>{content}</div>
				</Tooltip>
			) : (
				content
			)}
		</HeaderTableCell>
	);
};

export const TableLoader: FC<{ spanScope: number; type?: string }> = ({ spanScope, type }) => {
	const [params] = useSearchParams();
	const pageSizeParam = type ? `${type}PageSize` : 'pageSize';
	const pageSize = parseInt(params.get(pageSizeParam) || '8');

	return (
		<TableBody style={{ height: `${pageSize * 60}px` }}>
			<CustomTableRow>
				<RowTableCell colSpan={spanScope}>
					<div style={{ width: 'max-content', margin: 'auto' }}>
						<CircularProgress color="inherit" />
					</div>
				</RowTableCell>
			</CustomTableRow>
		</TableBody>
	);
};

export const TableNoData: FC<{ spanScope: number }> = ({ spanScope }) => (
	<TableBody>
		<CustomTableRow>
			<RowTableCell colSpan={spanScope}>
				<Text title="No results" fontWeight="semiBold" className="textAlignCenter" />
			</RowTableCell>
		</CustomTableRow>
	</TableBody>
);

export const pageSizeOptions = [
	{ id: '4', name: '4' },
	{ id: '8', name: '8' },
	{ id: '12', name: '12' },
	{ id: '25', name: '25' },
	{ id: '50', name: '50' },
];

export const PaginationStyled = styled(Pagination)(() => ({
	'.MuiPaginationItem-root': {
		padding: '10px',
		borderRadius: '8px',
		fontFamily: 'Inter',
		fontSize: '14px',
		fontWeight: 500,
		lineHeight: '20px',
		color: TEXT.SECONDARY,
		margin: 0,
	},
	'.Mui-selected': {
		backgroundColor: PRIMARY.QUATERNARY,
	},
}));

export const PaginationWrapper = styled('div')(() => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
}));
